import type { LandingPageFragment } from '~/jobiqo/models/entity';
import {
  DIMENSIONS,
  EventParameters,
  PAGE_TYPES,
  setEventParams,
} from '~/jobiqo/lib/analytics';
import type { OrganizationProfile } from '~/jobiqo/types/types';
import type {
  MiniSiteMenuItem,
  MiniSiteMenuTabValue,
  MiniSiteTabValue,
  OrganizationProfileWithMiniSiteMenu,
} from './types';

/**
 * Utility helpers for translating between mini-site domain data and tab state.
 *
 * The tab UI uses simple string values, but mini-sites now have two different
 * navigation concepts in one tab bar:
 * - static in-page tabs (`menu` and `jobs`),
 * - configured landing-page links from `organization.minisiteMenu`.
 *
 * Keeping the parsing/building logic here prevents every caller from needing to
 * know about the string prefix used for dynamic menu tabs.
 */
const MINI_SITE_MENU_TAB_VALUE_PREFIX = 'minisite-menu:';

/**
 * Return the canonical mini-site root path. The fallback prevents navigation
 * crashes if an organization is missing its URL data.
 */
export const getMiniSiteRootPath = (
  organization: OrganizationProfile,
): string => organization.url?.path ?? '/';

/**
 * Normalize paths before comparing menu entries.
 *
 * Drupal/menu data can include trailing slashes while Next.js routing usually
 * works with the same path without them. Normalizing avoids duplicate tabs like
 * `/company/acme/about` and `/company/acme/about/`.
 */
const normalizeMiniSitePath = (path?: string | null): string | null => {
  if (!path) {
    return null;
  }

  const normalizedPath = path.trim();

  if (!normalizedPath) {
    return null;
  }

  return normalizedPath === '/'
    ? normalizedPath
    : normalizedPath.replace(/\/$/, '');
};

/**
 * Convert the GraphQL mini-site menu payload into safe tab items.
 *
 * The backend can technically return incomplete entries, duplicate paths, or a
 * menu entry pointing back to the mini-site root. The root is already shown as
 * the permanent Home tab, so it is intentionally filtered out here.
 */
export const getMiniSiteMenuItems = (
  organization: OrganizationProfileWithMiniSiteMenu,
): MiniSiteMenuItem[] => {
  const rootPath = normalizeMiniSitePath(getMiniSiteRootPath(organization));
  const seenPaths = new Set<string>();

  return (organization.minisiteMenu ?? []).reduce<MiniSiteMenuItem[]>(
    (items, menuItem) => {
      const path = normalizeMiniSitePath(menuItem?.path);
      const title = menuItem?.landingPage?.title?.trim();

      if (!path || !title || path === rootPath || seenPaths.has(path)) {
        return items;
      }

      seenPaths.add(path);
      items.push({
        path,
        title,
        id: menuItem?.landingPage?.id,
      });

      return items;
    },
    [],
  );
};

/**
 * Build a tab value for a dynamic mini-site menu link.
 *
 * Prefixing the path avoids collisions with static tab values. For example, a
 * landing page path or slug should never be able to behave like the built-in
 * `jobs` or `menu` tabs by accident.
 */
export const getMiniSiteMenuTabValue = (path: string): MiniSiteMenuTabValue =>
  `${MINI_SITE_MENU_TAB_VALUE_PREFIX}${path}` as MiniSiteMenuTabValue;

/**
 * Extract the URL path from a dynamic mini-site menu tab value.
 *
 * Returns `null` for static tabs so callers can branch cleanly between in-page
 * tab state changes and full route navigation.
 */
export const getMiniSitePathFromTabValue = (
  tab: MiniSiteTabValue,
): string | null =>
  tab.startsWith(MINI_SITE_MENU_TAB_VALUE_PREFIX)
    ? tab.slice(MINI_SITE_MENU_TAB_VALUE_PREFIX.length)
    : null;

export const getMiniSiteLandingPageDimensions = (
  organization: OrganizationProfile,
  landingPage: LandingPageFragment,
  path: string,
): EventParameters =>
  setEventParams({
    [DIMENSIONS.entity_id]: landingPage.id,
    [DIMENSIONS.entity_type]: 'node',
    [DIMENSIONS.entity_bundle]: 'landing_page',
    [DIMENSIONS.entity_subtype]: 'minisite_landing_page',
    [DIMENSIONS.entity_title]: landingPage.title,
    [DIMENSIONS.title]: landingPage.title,
    [DIMENSIONS.route]: path,
    [DIMENSIONS.organization_id]: organization.organization?.id,
    [DIMENSIONS.organization_name]:
      organization.organization?.name ?? organization.name,
    [DIMENSIONS.owner_id]: organization.ownerId,
    [DIMENSIONS.page_type]: PAGE_TYPES.MINISITE,
  });
